import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from "@/integrations/supabase/client";
import { useAuthState } from "@/hooks/useAuthState";
import { toast } from "sonner";

export const useGDPRConsent = () => {
  const { user } = useAuthState();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [needsConsent, setNeedsConsent] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [profile, setProfile] = useState<any>(null);

  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    console.log('GDPR: Checking consent status for user:', user.id, 'on path:', location.pathname);
    checkConsentStatus();
  }, [user, location.pathname]);

  const checkConsentStatus = async () => {
    try {
      setIsLoading(true);
      console.log('GDPR: Fetching consent status from database for user:', user?.id);
      
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('data_processing_consent, gdpr_consent_given_at, gdpr_consent_version')
        .eq('id', user?.id)
        .single();

      if (error) {
        console.error('GDPR: Error fetching profile:', error);
        return;
      }

      console.log('GDPR: Profile data retrieved:', {
        data_processing_consent: profile?.data_processing_consent,
        gdpr_consent_given_at: profile?.gdpr_consent_given_at,
        gdpr_consent_version: profile?.gdpr_consent_version
      });

      setProfile(profile);
      
      // Check if user needs to give consent
      const hasConsent = profile?.data_processing_consent === true;
      console.log('GDPR: Has consent:', hasConsent, 'Needs consent:', !hasConsent);
      setNeedsConsent(!hasConsent);
    } catch (error) {
      console.error('GDPR: Error checking consent status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const giveConsent = async () => {
    if (!user) {
      console.error('GDPR: No user found when trying to give consent');
      return false;
    }

    try {
      setIsLoading(true);
      console.log('GDPR: Giving consent for user:', user.id);
      
      const { data, error } = await supabase
        .from('profiles')
        .update({
          data_processing_consent: true,
          gdpr_consent_given_at: new Date().toISOString(),
          gdpr_consent_version: 'v1.0'
        })
        .eq('id', user.id)
        .select('data_processing_consent, gdpr_consent_given_at, gdpr_consent_version');

      if (error) {
        console.error('GDPR: Database error when giving consent:', error);
        throw error;
      }

      console.log('GDPR: Consent update result:', data);

      // Verify the update was successful
      if (data && data.length > 0 && data[0].data_processing_consent === true) {
        console.log('GDPR: Consent successfully saved to database');
        
        // Update local state immediately
        setNeedsConsent(false);
        setProfile(prev => prev ? {
          ...prev,
          data_processing_consent: true,
          gdpr_consent_given_at: new Date().toISOString(),
          gdpr_consent_version: 'v1.0'
        } : null);
        
        // Invalidate React Query cache to force fresh data fetch
        console.log('GDPR: Invalidating profile cache after consent update');
        queryClient.invalidateQueries({ queryKey: ['profile'] });
        queryClient.invalidateQueries({ queryKey: ['user-profile'] });
        
        toast.success("Samtycke registrerat framgångsrikt.");
        
        // Double-check by re-fetching from database
        setTimeout(() => {
          console.log('GDPR: Re-checking consent status after update');
          checkConsentStatus();
        }, 500);
        
        return true;
      } else {
        console.error('GDPR: Consent update failed - no data returned or consent not set to true');
        throw new Error('Failed to update consent in database');
      }
    } catch (error) {
      console.error('GDPR: Error giving consent:', error);
      toast.error("Ett fel uppstod vid registrering av samtycke.");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    needsConsent,
    isLoading,
    profile,
    giveConsent,
    checkConsentStatus
  };
};